import { getLocaleString, hasAnyTranslation } from '@assets/i18n/config';
import type { FinancialProductStrategy } from '@generated/graphql';

import type { TranslatedString } from '@interfaces/shared';
import type { TFunction } from 'i18next';
import { round } from 'lodash-es';
import {
  HOUSEHOLD_MODEL_PORTFOLIO_CONFIGURABLE_DETAIL_FIELDS,
  HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE,
  IPS_FIELD_OPTIONS,
  MODEL_DETAIL_FIELD_DEFAULTS,
  MODEL_DETAIL_FIELD_OPTIONS,
} from './householdModelPortfolio.constants';
import { isIpsFieldEnabled } from './householdModelPortfolio.ips.utils';
import type {
  HouseholdModelPortfolioAction,
  HouseholdModelPortfolioChild,
  HouseholdModelPortfolioConfigurableDetailOptionKey,
  HouseholdModelPortfolioModel,
  HouseholdModelPortfolioModelDetailField,
  HouseholdModelPortfolioWidgetOptions,
} from './householdModelPortfolio.types';

export { IPS_FIELD_OPTIONS as IPS_FIELD_SHOW_OPTIONS } from './householdModelPortfolio.constants';

export { MODEL_DETAIL_FIELD_OPTIONS };

export const getHouseholdModelPortfolioAction = (
  attachedFinancialProductId?: string | null,
): HouseholdModelPortfolioAction =>
  attachedFinancialProductId ? 'edit' : 'initialize';

type HouseholdModelPortfolioDetailsStateInput = {
  action: HouseholdModelPortfolioAction;
  model: HouseholdModelPortfolioModel | null;
  modelDetailsLoading: boolean;
  modelDetailsError?: Error;
};

/** Attached model id exists but fetchModelPortfolio returned no usable portfolio. */
export const isHouseholdModelPortfolioDetailsMissing = ({
  action,
  model,
  modelDetailsLoading,
  modelDetailsError,
}: HouseholdModelPortfolioDetailsStateInput): boolean =>
  action === 'edit' && !modelDetailsLoading && !modelDetailsError && !model;

export const hasAnyIpsFieldEnabled = (
  options: HouseholdModelPortfolioWidgetOptions = {},
): boolean =>
  IPS_FIELD_OPTIONS.some((optionKey) => isIpsFieldEnabled(options, optionKey));

export const isModelDetailFieldEnabled = (
  options: HouseholdModelPortfolioWidgetOptions = {},
  optionKey: HouseholdModelPortfolioConfigurableDetailOptionKey,
): boolean => options[optionKey] ?? MODEL_DETAIL_FIELD_DEFAULTS[optionKey];

export const getEnabledModelDetailOptionKeys = (
  options: HouseholdModelPortfolioWidgetOptions = {},
): HouseholdModelPortfolioConfigurableDetailOptionKey[] =>
  MODEL_DETAIL_FIELD_OPTIONS.filter((optionKey) =>
    isModelDetailFieldEnabled(options, optionKey),
  );

export const getHouseholdModelPortfolioCashEquivalentChild = (
  model: HouseholdModelPortfolioModel,
): HouseholdModelPortfolioChild | undefined =>
  model.children?.find((child) => child.isCashEquivalent);

export const formatHouseholdModelPortfolioTranslatedValue = (
  translateBackendString: (translatedObject: unknown) => string,
  value?: TranslatedString | null,
): string => {
  const translated = translateBackendString(value ?? undefined);
  return translated.trim()
    ? translated
    : HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE;
};

export const formatHouseholdModelPortfolioStrategy = (
  strategy: FinancialProductStrategy | undefined,
  t: TFunction<['modelPortfolios']>,
): string => {
  if (!strategy) {
    return HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE;
  }

  return t(`modelPortfolios:strategy.${strategy}`);
};

export const formatHouseholdModelPortfolioBoolean = (
  value: boolean | undefined,
  t: TFunction<['shared']>,
): string => {
  if (value === undefined) {
    return HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE;
  }

  return value ? t('shared:yes') : t('shared:no');
};

export const formatHouseholdModelPortfolioNumber = (
  value: number | undefined,
): string => {
  if (value === undefined || Number.isNaN(value)) {
    return HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE;
  }

  return new Intl.NumberFormat(getLocaleString(), {
    maximumFractionDigits: 2,
  }).format(round(value, 2));
};

export const formatHouseholdModelPortfolioCashEquivalentSecurity = (
  translateBackendString: (translatedObject: unknown) => string,
  model: HouseholdModelPortfolioModel,
): string => {
  const cashEquivalentChild =
    getHouseholdModelPortfolioCashEquivalentChild(model);
  if (!cashEquivalentChild?.financialProduct) {
    return HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE;
  }

  const ticker = cashEquivalentChild.financialProduct.ticker?.trim();
  const name = formatHouseholdModelPortfolioTranslatedValue(
    translateBackendString,
    cashEquivalentChild.financialProduct.translatedName,
  );

  if (name === HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE) {
    return HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE;
  }

  return ticker ? `${ticker} - ${name}` : name;
};

type ConfigurableModelDetailFieldConfig = {
  optionKey: HouseholdModelPortfolioConfigurableDetailOptionKey;
  labelKey: string;
  labelOptionKey?: 'modelStrategyLabel' | 'modelNameLabel' | 'modelFamilyLabel';
};

/**
 * Model Strategy / Name / Model Family support org-configured label overrides
 * (`modelStrategyLabel` / `modelNameLabel` / `modelFamilyLabel`), unlike the other
 * configurable Model tab fields — mirrors `getHouseholdModelPortfolioIpsFieldLabel`.
 */
const getConfigurableModelDetailFieldLabel = (
  translateBackendString: (translatedObject: unknown) => string,
  fieldConfig: ConfigurableModelDetailFieldConfig | undefined,
  fallbackLabel: string,
  options: HouseholdModelPortfolioWidgetOptions,
): string => {
  const labelOptionKey = fieldConfig?.labelOptionKey;
  if (!labelOptionKey) {
    return fallbackLabel;
  }

  const customLabel = options[labelOptionKey];
  if (customLabel && hasAnyTranslation(customLabel)) {
    const translated = translateBackendString(customLabel);
    if (translated.trim()) {
      return translated;
    }
  }

  return fallbackLabel;
};

const getConfigurableModelDetailField = (
  translateBackendString: (translatedObject: unknown) => string,
  optionKey: HouseholdModelPortfolioConfigurableDetailOptionKey,
  model: HouseholdModelPortfolioModel,
  t: TFunction<['modelPortfolios', 'shared']>,
  options: HouseholdModelPortfolioWidgetOptions,
): HouseholdModelPortfolioModelDetailField => {
  const fieldConfig = HOUSEHOLD_MODEL_PORTFOLIO_CONFIGURABLE_DETAIL_FIELDS.find(
    (config) => config.optionKey === optionKey,
  );
  const fallbackLabel = fieldConfig
    ? t(fieldConfig.labelKey as 'modelPortfolios:portfolio.details.name')
    : optionKey;
  const label = getConfigurableModelDetailFieldLabel(
    translateBackendString,
    fieldConfig,
    fallbackLabel,
    options,
  );

  switch (optionKey) {
    // These three sat on the tab before they were configurable, and the id is
    // what names a field in the DOM — it is the React key and the test id. Left
    // as they were rather than renamed to the option keys the fields below use,
    // so anything already looking for them still finds them.
    case 'showModelStrategy':
      return {
        id: 'strategy',
        label,
        value: formatHouseholdModelPortfolioStrategy(model.strategy, t),
      };
    case 'showModelName':
      return {
        id: 'name',
        label,
        value: formatHouseholdModelPortfolioTranslatedValue(
          translateBackendString,
          model.translatedName,
        ),
      };
    case 'showModelFamily':
      return {
        id: 'modelFamily',
        label,
        value: formatHouseholdModelPortfolioTranslatedValue(
          translateBackendString,
          model.theme?.translatedName,
        ),
      };
    case 'showRiskProfile':
      return {
        id: optionKey,
        label,
        value: formatHouseholdModelPortfolioTranslatedValue(
          translateBackendString,
          model.riskProfileName,
        ),
      };
    case 'showRiskReturnLevel':
      return {
        id: optionKey,
        label,
        value: formatHouseholdModelPortfolioNumber(model.riskReturnLevel),
      };
    case 'showSuggestible':
      return {
        id: optionKey,
        label,
        value: formatHouseholdModelPortfolioBoolean(model.suggestable, t),
      };
    case 'showZeroPercentCash':
      return {
        id: optionKey,
        label,
        value: formatHouseholdModelPortfolioBoolean(model.zeroPercentCash, t),
      };
    case 'showCashEquivalentSecurity':
      return {
        id: optionKey,
        label,
        value: formatHouseholdModelPortfolioCashEquivalentSecurity(
          translateBackendString,
          model,
        ),
      };
    case 'showLowThresholdScore':
      return {
        id: optionKey,
        label,
        value: formatHouseholdModelPortfolioNumber(model.lowThresholdScore),
      };
    case 'showMinimumScore':
      return {
        id: optionKey,
        label,
        value: formatHouseholdModelPortfolioNumber(model.suggestedMinScore),
      };
    case 'showMaximumScore':
      return {
        id: optionKey,
        label,
        value: formatHouseholdModelPortfolioNumber(model.suggestedMaxScore),
      };
    case 'showForecastedRateOfReturn':
      return {
        id: optionKey,
        label,
        value: formatHouseholdModelPortfolioNumber(
          model.forecastedRateOfReturn,
        ),
      };
    case 'showForecastedStandardDeviation':
      return {
        id: optionKey,
        label,
        value: formatHouseholdModelPortfolioNumber(
          model.forecastedStandardDeviation,
        ),
      };
    case 'showFundamentalDetailsLink': {
      const href = formatHouseholdModelPortfolioTranslatedValue(
        translateBackendString,
        model.translatedUrl,
      );
      const hasHref = href !== HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE;

      return {
        id: optionKey,
        label,
        value: hasHref ? href : HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE,
        href: hasHref ? href : undefined,
      };
    }
    default:
      return {
        id: optionKey,
        label,
        value: HOUSEHOLD_MODEL_PORTFOLIO_EMPTY_DETAIL_VALUE,
      };
  }
};

export const buildHouseholdModelPortfolioModelDetailFields = (
  translateBackendString: (translatedObject: unknown) => string,
  model: HouseholdModelPortfolioModel | null,
  options: HouseholdModelPortfolioWidgetOptions = {},
  t: TFunction<['modelPortfolios', 'shared']>,
): HouseholdModelPortfolioModelDetailField[] => {
  if (!model) {
    return [];
  }

  return getEnabledModelDetailOptionKeys(options).map((optionKey) =>
    getConfigurableModelDetailField(
      translateBackendString,
      optionKey,
      model,
      t,
      options,
    ),
  );
};

export const getHouseholdModelPortfolioWidgetTitle = (
  translateBackendString: (translatedObject: unknown) => string,
  options: HouseholdModelPortfolioWidgetOptions | undefined,
  t: TFunction<['pageConfiguration']>,
): string =>
  options?.customTitle
    ? translateBackendString(options.customTitle)
    : t('pageConfiguration:widgetTypes.HOUSEHOLD_MODEL_PORTFOLIO');

export const getHouseholdModelPortfolioActionLabel = (
  action: HouseholdModelPortfolioAction,
  t: TFunction<['components']>,
): string =>
  action === 'edit'
    ? t('components:householdModelPortfolio.edit')
    : t('components:householdModelPortfolio.initialize');
